import React, { useEffect, useState } from "react";
import { Checkbox, Input, Modal, Table } from "antd";
import { SimpleSecurityDefinitionAdminDto } from "../../../services/ApiTypes";
import "./AccessDefinitionModal.css";

interface AccessDefinitionModalProps {
  onClose: () => void;
  onSave: (updatedData: SimpleSecurityDefinitionAdminDto) => void;
  simpleSecurityDefinition: SimpleSecurityDefinitionAdminDto;
}

const AccessDefinitionModal: React.FC<AccessDefinitionModalProps> = ({
  onClose,
  onSave,
  simpleSecurityDefinition,
}) => {
  const [editableData, setEditableData] =
    useState<SimpleSecurityDefinitionAdminDto>(simpleSecurityDefinition);

  const [inputEnabled, setInputEnabled] = useState<
    Record<keyof SimpleSecurityDefinitionAdminDto, boolean>
  >({
    name: !!simpleSecurityDefinition.name,
    surname: !!simpleSecurityDefinition.surname,
    email: !!simpleSecurityDefinition.email,
    phoneNumber: !!simpleSecurityDefinition.phoneNumber,
    additionalOne: !!simpleSecurityDefinition.additionalOne,
    additionalTwo: !!simpleSecurityDefinition.additionalTwo,
    id: false,
    users: false,
  });

  const fields: {
    label: string;
    field: keyof SimpleSecurityDefinitionAdminDto;
  }[] = [
    { label: "Name", field: "name" },
    { label: "Surname", field: "surname" },
    { label: "Email", field: "email" },
    { label: "Phone Number", field: "phoneNumber" },
    { label: "Additional Info 1", field: "additionalOne" },
    { label: "Additional Info 2", field: "additionalTwo" },
  ];

  const handleCheckboxChange = (
    field: keyof SimpleSecurityDefinitionAdminDto,
    checked: boolean
  ) => {
    setInputEnabled((prev) => ({ ...prev, [field]: checked }));
    if (checked && !editableData[field]) {
      setEditableData((prev) => ({
        ...prev,
        [field]: fields.find((f) => f.field === field)?.label || "",
      }));
    } else {
      setEditableData((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleChange = (
    field: keyof SimpleSecurityDefinitionAdminDto,
    value: string
  ) => {
    setEditableData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    setEditableData(simpleSecurityDefinition);
    setInputEnabled({
      name: !!simpleSecurityDefinition.name,
      surname: !!simpleSecurityDefinition.surname,
      email: !!simpleSecurityDefinition.email,
      phoneNumber: !!simpleSecurityDefinition.phoneNumber,
      additionalOne: !!simpleSecurityDefinition.additionalOne,
      additionalTwo: !!simpleSecurityDefinition.additionalTwo,
      id: false,
      users: false,
    });
    onClose();
  };

  const handleSave = () => {
    onSave(editableData);
  };

  
  useEffect(() => {
    setEditableData(simpleSecurityDefinition)
    setInputEnabled({
      name: !!simpleSecurityDefinition.name,
      surname: !!simpleSecurityDefinition.surname,
      email: !!simpleSecurityDefinition.email,
      phoneNumber: !!simpleSecurityDefinition.phoneNumber,
      additionalOne: !!simpleSecurityDefinition.additionalOne,
      additionalTwo: !!simpleSecurityDefinition.additionalTwo,
      id: false,
      users: false,
    });
  }, [simpleSecurityDefinition]);

  const columns = [
    {
      title: "Enabled",
      dataIndex: "enabled",
      render: (_: any, record: any) => (
        <Checkbox
          checked={
            inputEnabled[record.field as keyof SimpleSecurityDefinitionAdminDto]
          }
          onChange={(e) =>
            handleCheckboxChange(
              record.field as keyof SimpleSecurityDefinitionAdminDto,
              e.target.checked
            )
          }
        />
      ),
    },
    {
      title: "Field Type",
      dataIndex: "label",
    },
    {
      title: "Display Name",
      dataIndex: "field",
      render: (field: keyof SimpleSecurityDefinitionAdminDto) => (
        <Input
          value={(editableData[field] as string) || ""}
          onChange={(e) => handleChange(field, e.target.value)}
          disabled={!inputEnabled[field]}
          data-testid={("override-input-" + field) || ""}
        />
      ),
    },
  ];

  const dataSource = fields.map(({ label, field }) => ({
    key: field,
    label,
    field,
  }));
  
  const isSaveDisabled = !Object.values(inputEnabled).some((enabled) => enabled);

  return (
    <Modal
      title="Access Definition"
      open={true}
      onCancel={handleCancel}
      onOk={handleSave}
      okText="Ok"
      cancelText="Cancel"
      width={650}
      className="custom-modal"
      okButtonProps={{ disabled: isSaveDisabled }}
    >
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowKey="key"
        size="small"
      />
      {isSaveDisabled && (
        <span className="error-message">
          At least one field must be selected.
        </span>
      )}
    </Modal>
  );
};

export default AccessDefinitionModal;
